import icFeatures from '@/assets/icons/managementArea/features.svg';
import icFeaturesActive from '@/assets/icons/managementArea/features-active.svg';
import icSupport from '@/assets/icons/managementArea/support.svg';
import icSupportActive from '@/assets/icons/managementArea/support-active.svg';
import icSupporteds from '@/assets/icons/managementArea/supporteds.svg';
import icSupportedsActive from '@/assets/icons/managementArea/supporteds-active.svg';
import icFunds from '@/assets/icons/managementArea/funds.svg';
import icFundsActive from '@/assets/icons/managementArea/funds-active.svg';
import icSettings from '@/assets/icons/managementArea/settings.svg';
import icSettingsActive from '@/assets/icons/managementArea/settings-active.svg';
import icFinance from '@/assets/icons/managementArea/finance.svg';
import icFinanceActive from '@/assets/icons/managementArea/finance-active.svg';
import icUsers from '@/assets/icons/managementArea/users.svg';
import icUsersActive from '@/assets/icons/managementArea/users-active.svg';
import icFeedback from '@/assets/icons/managementArea/feedback.svg';
import icFeedbackActive from '@/assets/icons/managementArea/feedback-active.svg';
import icProjects from '@/assets/icons/managementArea/projects.svg';
import icProjectsActive from '@/assets/icons/managementArea/projects-active.svg';
import idIcon from '@/assets/icons/id2.svg';

export interface ManagementAreaSidebarItem {
  text: string;
  iconSrc?: string;
  activeIconSrc?: string;
  to?: string;
  /** Absolute URL, opened in a new tab. Mutually exclusive with `to`. */
  href?: string;
  children?: ManagementAreaSidebarItem[];
  /** Key used to look up a dynamic badge count from useManagementBadges */
  badgeKey?: string;
  /** Shown beside the count, to say what it counts (HAT-1428). In the icon rail the badge is a dot. */
  badgeIcon?: string;
  /** An i18n key with _one/_other that says in words what the badge counts - its bubble on hover. */
  badgeTooltip?: string;
  /**
   * אם מוגדר, הפריט מוצג רק כשהדגל הזה דלוק למשתמש הנוכחי, ולא מוצג בכלל אחרת
   * (ולא מטושטש כמו פריט שהנתיב שלו כבוי).
   *
   * ⚠️ הערכה **לפי משתמש** ולא לפי קרן: לאזור הניהול אין קרן נבחרת בכלל, והוא
   * מותקן מחוץ לנתיבי הקרן. המפה מגיעה מ-GlobalFeatureFlagsProvider דרך
   * api/feature-flags. המשמעות המעשית: אפשר לשחרר מסך ניהול לפי מייל או לפי
   * תפקיד, אבל לא לפי קרן - וזה נכון למסך שלא שייך לאף קרן.
   *
   * קבוצה שכל ילדיה הוסתרו כך נעלמת גם היא, כדי שלא תישאר בתפריט קבוצה שנפתחת
   * לכלום.
   */
  featureKey?: string;
}

// User reports are triaged entirely in Sprints, so the only thing left here is
// a way out to that board. Empty on sites that report to a different board.
const sprintsBoardUrl: string = import.meta.env.VITE_APP_SPRINTS_BOARD_URL ?? '';

// The whole management area is available to Admins and Debuggers alike;
// entry to the area itself is gated by ManagementAreaProvider.
export const managementAreaSidebarItems: ManagementAreaSidebarItem[] = [
  {
    text: 'supportRequests',
    iconSrc: icSupport,
    activeIconSrc: icSupportActive,
    to: '/management/support-requests',
  },
  {
    text: 'projects.sidebarTitle',
    iconSrc: icProjects,
    activeIconSrc: icProjectsActive,
    children: [
      { text: 'projectsManagement.sidebarTitle', to: '/management/projects' },
      { text: 'suppliersApproval.sidebarTitle', to: '/management/projects/suppliers' },
    ],
  },
  {
    text: 'managementArea.groups.supporteds',
    iconSrc: icSupporteds,
    activeIconSrc: icSupportedsActive,
    // On the group too while it is closed: it opens collapsed, and a waiting request must show
    // without opening it
    badgeKey: 'israeliIdChangeRequests',
    badgeIcon: idIcon,
    // The same words as in the coordinator's menu
    badgeTooltip: 'israeliIdChangeRequest.navBadge',
    children: [
      { text: 'classifications', to: '/management/classifications' },
      { text: 'children', to: '/management/children' },
      // HAT-1428: coordinators' requests to correct a supported's ID are decided on this board.
      // The ID-card icon says what the count is (Chaya chose it, 20.10).
      {
        text: 'supporteds',
        to: '/management/supporteds',
        badgeKey: 'israeliIdChangeRequests',
        badgeIcon: idIcon,
        badgeTooltip: 'israeliIdChangeRequest.navBadge',
      },
      // HAT-1446 phase 5: a kollel groups existing supporteds, so its approval queue lives here
      // rather than getting its own top-level group. Gated by the same flag as the coordinator
      // tab - no point showing an empty queue on a site where the module isn't rolled out.
      { text: 'kollelsManagement.sidebarTitle', to: '/management/kollels', featureKey: 'kollels' },
      // HAT-1446 phase 6: the monthly request-folder approval queue - a kollel's OWN consolidated
      // money request, separate from the kollel's own welfare-committee approval above.
      { text: 'kollelFoldersManagement.sidebarTitle', to: '/management/kollels/folders', featureKey: 'kollels' },
    ],
  },
  {
    text: 'managementArea.groups.funds',
    iconSrc: icFunds,
    activeIconSrc: icFundsActive,
    children: [
      { text: 'fundsManagement.sidebarTitle', to: '/management/funds' },
      { text: 'openFundRequests.title', to: '/management/open-fund-requests' },
      // Lead coordinators whose Israeli ID is on a record under another name wait here for the office (29.10.2026).
      {
        text: 'leadCoordinatorsReview.sidebar',
        to: '/management/lead-coordinators-review',
        badgeKey: 'leadCoordinatorsReview',
        featureKey: 'fund-representatives',
      },
      // A lead coordinator's request to add a coordinator waits here for the office (SPR-10486).
      {
        text: 'coordinatorAddRequests.sidebar',
        to: '/management/coordinator-add-requests',
        badgeKey: 'coordinatorAddRequests',
        featureKey: 'fund-representatives',
      },
      // «בקשות הסרה מהפרויקט» and «מינוי מייסדים» are gone (menu 15.10.2026, pages 3.11.2026): a
      // coordinator leaves on their own, the office removes or appoints a lead coordinator on a
      // fund's «רכזים», and the first appointment of lead coordinators was run once by us.
    ],
  },
  {
    text: 'managementArea.groups.systemSettings',
    iconSrc: icSettings,
    activeIconSrc: icSettingsActive,
    children: [
      { text: 'holidayPeriodsManagement.title', to: '/management/holiday-periods' },
      { text: 'suppliersManagement.sidebarTitle', to: '/management/suppliers' },
      { text: 'activityDomainsManagement.sidebarTitle', to: '/management/activity-domains' },
      { text: 'projectTypesManagement.sidebarTitle', to: '/management/project-types' },
      { text: 'eligibilityOverrides.sidebarTitle', to: '/management/eligibility-overrides' },
      { text: 'thirdPartyAuthorizations.sidebarTitle', to: '/management/third-party-payment-authorizations' },
      { text: 'landingSiteForms.sidebar', to: '/management/landing-site-forms' },
    ],
  },
  {
    text: 'managementArea.groups.finance',
    iconSrc: icFinance,
    activeIconSrc: icFinanceActive,
    children: [
      { text: 'transactionsInquiry.title', to: '/management/transactions-inquiry' },
      { text: 'donorDonationsReport.title', to: '/management/donor-donations-report' },
      // HAT-10024: a donor's bank standing orders in every fund, for a bank return that covers several.
      { text: 'donorStandingOrders.sidebar', to: '/management/donor-standing-orders', featureKey: 'donor-bank-standing-orders' },
      { text: 'donations.management.sidebarTitle', to: '/management/offline-donations' },
      // HAT-1441: the office locks a collection date here before sending its Masav file. Only where
      // coordinators can change bank standing orders at all - the same flag as those actions.
      { text: 'bankCollectionLocks.sidebar', to: '/management/bank-collection-locks', featureKey: 'bank-standing-order-actions' },
      { text: 'nedarimCredentials.title', to: '/management/nedarim-credentials' },
      { text: 'bulkUploads.sidebar', to: '/management/bulk-uploads' },
      { text: 'nedarimSync.sidebar', to: '/management/nedarim-sync' },
      { text: 'nedarimTransactionsFundAllocation.sidebar', to: '/management/nedarim-transactions-fund-allocation' },
      {
        text: 'paymentDevices.sidebar',
        to: '/management/devices',
        badgeKey: 'managementPaymentDeviceRequests',
        featureKey: 'device-rentals',
      },
    ],
  },
  {
    text: 'managementArea.groups.users',
    iconSrc: icUsers,
    activeIconSrc: icUsersActive,
    children: [
      { text: 'usersManagement.sidebarTitle', to: '/management/users' },
      { text: 'loginAudit.sidebarTitle', to: '/management/login-audit' },
    ],
  },
  {
    text: 'managementArea.groups.features',
    iconSrc: icFeatures,
    activeIconSrc: icFeaturesActive,
    children: [
      { text: 'featureFlags.sidebar', to: '/management/feature-flags' },
      // Beside the flags, not under «הגדרות מערכת»: an announcement is written and released with its feature (HAT-1489).
      { text: 'systemMessages.sidebar', to: '/management/system-messages' },
      { text: 'fundGroups.title', to: '/management/fund-groups' },
    ],
  },
  {
    text: 'managementArea.groups.feedback',
    iconSrc: icFeedback,
    activeIconSrc: icFeedbackActive,
    children: [
      ...(sprintsBoardUrl ? [{ text: 'sprintsBoard', href: sprintsBoardUrl }] : []),
      /*
       * מוסר מהתפריט בבקשת המשרד - ראה SeedFeatureFlag_Inquiries.sql. הדגל נזרע
       * NotReleased, ולכן הפריט (ואיתו הקבוצה, שזה ילדה היחיד באתר שאין לו לוח
       * Sprints) לא מוצג עד שמדליקים אותו במסך הפיצ'רים.
       */
      { text: 'inquiries.management.sidebar', to: '/management/inquiries', badgeKey: 'managementInquiries', featureKey: 'inquiries' },
    ],
  },
];
